import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { CheckoutSuccess, ExternalSalesApiError, submitCheckout } from '../../api/externalSalesApi';
import { Utm } from '../../api/types';
import { persistCampaignCode } from '../../campaignSession';
import { EXTERNAL_SALES_BASE_PATH } from '../../config';
import { delay } from '../../delay';
import { generateBrowserNonce } from './browserNonce';
import { fullPageRedirect } from './browserRedirect';

// webshop-api appends the claim token to the success URL; never add one here.
const buildSuccessUrl = () => `${window.location.origin}${EXTERNAL_SALES_BASE_PATH}/success`;
const buildCancelUrl = (campaignCode: string) => `${window.location.origin}${EXTERNAL_SALES_BASE_PATH}/c/${encodeURIComponent(campaignCode)}/checkout`;

export type CheckoutState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'redirecting' }
  | { kind: 'rejected' }
  | { kind: 'error' };

export type UseCheckoutParams = {
  campaignCode: string;
  sku: string;
  utm: Utm;
};

export type UseCheckoutResult = {
  state: CheckoutState;
  submit: (email: string) => void;
};

const MAX_PENDING_POLLS = 5;
const PENDING_POLL_DELAY_MS = 1000;
const MAX_NONCE_REFRESHES = 1;

export function useCheckout({ campaignCode, sku, utm }: UseCheckoutParams): UseCheckoutResult {
  const [state, setState] = useState<CheckoutState>({ kind: 'idle' });
  const mounted = useRef(true);

  useEffect(() => () => {
    mounted.current = false;
  }, []);

  const submit = useCallback((email: string) => {
    setState({ kind: 'submitting' });

    // Fresh nonce per attempt (this click); reused across 202 polls, re-minted on 409.
    const attempt = async (
      nonce: string,
      nonceRefreshes: number,
      pendingPolls: number,
    ): Promise<void> => {
      let outcome: CheckoutSuccess;
      try {
        outcome = await submitCheckout({
          campaignCode,
          items: [{ sku }],
          email,
          utm,
          browserNonce: nonce,
          successUrl: buildSuccessUrl(),
          cancelUrl: buildCancelUrl(campaignCode),
        });
      } catch (error) {
        const status = error instanceof ExternalSalesApiError ? error.status : undefined;
        if (status === 409 && nonceRefreshes < MAX_NONCE_REFRESHES) {
          return attempt(generateBrowserNonce(), nonceRefreshes + 1, pendingPolls);
        }
        if (mounted.current) {
          setState(status === 422 ? { kind: 'rejected' } : { kind: 'error' });
        }
        return undefined;
      }

      if (outcome.status === 'redirect') {
        if (mounted.current) {
          setState({ kind: 'redirecting' });
        }
        persistCampaignCode(campaignCode);
        fullPageRedirect(outcome.redirectUrl);
        return undefined;
      }

      if (pendingPolls + 1 > MAX_PENDING_POLLS) {
        if (mounted.current) {
          setState({ kind: 'error' });
        }
        return undefined;
      }

      await delay(PENDING_POLL_DELAY_MS);
      return attempt(nonce, nonceRefreshes, pendingPolls + 1);
    };

    attempt(generateBrowserNonce(), 0, 0).catch(() => undefined);
  }, [campaignCode, sku, utm]);

  return { state, submit };
}
